import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { colors, spacing } from '../constants';
import { useAuth } from '../context/AuthContext';
import { useEscalatedQueriesAuth } from '../hooks/queries/escalatedQueries/useEscalatedQueriesAuth';
import { useEscalatedQueriesPageState } from '../hooks/queries/escalatedQueries/useEscalatedQueriesPageState';
import { useEscalatedQueriesPageData } from '../hooks/queries/escalatedQueries/useEscalatedQueriesPageData';
import { useEscalatedQueriesPageFilters } from '../hooks/queries/escalatedQueries/useEscalatedQueriesPageFilters';
import { useEscalatedQueriesPageModals } from '../hooks/queries/escalatedQueries/useEscalatedQueriesPageModals';
import { REGION_OPTIONS, STATUS_CARD_CONFIG } from '../constants/escalatedQueriesPage';
import { getRegionColor } from '../constants/raiseQueryPage';
import SearchAndFilter from '../components/queries/SearchAndFilter';
import ViewQueryModal from '../components/queries/ViewQueryModal';
import RegionCardsGrid from '../components/raiseQueryPage/RegionCardsGrid';
import EscalatedQueriesTable from '../components/escalatedQueriesPage/EscalatedQueriesTable';

const EscalatedQueries = () => {
  const { user, loading: authLoading } = useAuth();
  const tableContainerRef = useRef(null);

  // Handle GM user redirect
  useEscalatedQueriesAuth(user, authLoading);

  // Initialize all hooks
  const pageState = useEscalatedQueriesPageState();

  const { fetchQueries, projectsCatalog } = useEscalatedQueriesPageData({
    searchQuery: pageState.searchQuery,
    filters: pageState.filters,
    setAllQueries: pageState.setAllQueries,
    setLoading: pageState.setLoading,
    setProjectOptions: pageState.setProjectOptions,
    setQueryTypeOptions: pageState.setQueryTypeOptions,
    setStatusSummary: pageState.setStatusSummary,
    setBackendCounts: pageState.setBackendCounts,
    setEdUsersByRegion: pageState.setEdUsersByRegion,
    setCurrentUser: pageState.setCurrentUser,
  });

  const {
    filteredQueries,
    paginatedQueries,
    groupedQueriesByProject,
    paginatedProjects,
    totalProjects,
  } = useEscalatedQueriesPageFilters({
    allQueries: pageState.allQueries,
    selectedRegionCard: pageState.selectedRegionCard,
    selectedStatusCard: pageState.selectedStatusCard,
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    backendCounts: pageState.backendCounts,
    setQueries: pageState.setQueries,
    setTotal: pageState.setTotal,
    setStatusSummary: pageState.setStatusSummary,
    setRegionStatusSummary: pageState.setRegionStatusSummary,
    setPage: pageState.setPage,
  });

  const modals = useEscalatedQueriesPageModals();

  // Handlers
  const handleRegionCardClick = (regionCode) => {
    if (pageState.selectedRegionCard === regionCode) {
      pageState.setSelectedRegionCard(null);
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(null);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleStatusCardClick = (statusLabel, regionCode, ref) => {
    const isStatusSelected = pageState.selectedRegionCard === regionCode && pageState.selectedStatusCard === statusLabel;
    if (isStatusSelected) {
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(statusLabel);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleView = (query) => {
    modals.handleOpenViewModal(query);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        {/* Region Cards Grid */}
        <RegionCardsGrid
          visibleRegionOptions={REGION_OPTIONS}
          regionStatusSummary={pageState.regionStatusSummary}
          edUsersByRegion={pageState.edUsersByRegion}
          selectedRegionCard={pageState.selectedRegionCard}
          selectedStatusCard={pageState.selectedStatusCard}
          onRegionCardClick={handleRegionCardClick}
          onStatusCardClick={handleStatusCardClick}
          tableContainerRef={tableContainerRef}
        />

        {/* Search and Filter */}
        <Box sx={{ mb: spacing.md }}>
          <SearchAndFilter
            onSearchChange={pageState.handleSearchChange}
            onFilterChange={pageState.handleFilterChange}
            searchValue={pageState.searchQuery}
            filters={pageState.filters}
            defaultExpanded={false}
            showStatusFilter={false}
            showPriorityFilter={false}
            showProjectFilter={true}
            showLifecycleStatusFilter={false}
            showAssignedFilter={false}
            showQueryTypeFilter={true}
            projectOptions={pageState.projectOptions}
            queryTypeOptions={pageState.queryTypeOptions}
            placeholder="Search notified queries by ID, title, description, or project..."
            onRefresh={fetchQueries}
            refreshTooltip="Refresh notified queries"
            refreshing={pageState.loading}
          />
        </Box>

        {/* Query Table */}
        <EscalatedQueriesTable
          loading={pageState.loading}
          total={pageState.total}
          paginatedProjects={paginatedProjects}
          groupedQueriesByProject={groupedQueriesByProject}
          expandedProjects={pageState.expandedProjects}
          projectsCatalog={projectsCatalog}
          onToggleProject={pageState.toggleProject}
          onView={handleView}
          page={pageState.page}
          rowsPerPage={pageState.rowsPerPage}
          handleChangePage={pageState.handleChangePage}
          handleChangeRowsPerPage={pageState.handleChangeRowsPerPage}
          tableContainerRef={tableContainerRef}
        />
      </Box>

      {/* View Query Modal */}
      <ViewQueryModal
        open={modals.viewModalOpen}
        onClose={modals.handleCloseViewModal}
        queryId={modals.selectedQuery?._id}
        query={modals.selectedQuery}
      />
    </Box>
  );
};

export default EscalatedQueries;
